<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <b-dropdown variant="link" no-caret toggle-class="p-0 mr-2">
                <template #button-content>
                    <font-awesome-icon class="text-secondary" :icon="['fas', 'ellipsis-vertical']" />
                </template>
                <b-dropdown-item :key="filter.filter" v-for="filter in filters" @click="setFilter(filter.filter)">
                    {{ filter.title }}
                </b-dropdown-item>
            </b-dropdown>
        </template>
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <card-grid
                ref="grid"
                :key="cardGridKey"
                :header-height="50"
                :is-search="false"
                :columnDefs="columnDefs"
                :rowData="rowData"
                :pagination="true"
                :pagination-page-size="limit"
                :cache-block-size="limit"
                :is-auto-width="true"
                :font-size="tabWidget.settings.table_font_size"
            ></card-grid>
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :withTableFontSize="true"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../../../api';
    import CardGrid from '../../../generic/card-grid/card-grid.vue';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import CardGridItem from '../common/card-grid-item.vue';
    import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
    import NoteCellRenderer from '../../../reports/guest-transactions/note-cell-renderer.vue';
    import { REPORT_GUEST_TRANSACTIONS_ENDPOINT } from '../../../../endpoints';
    import { getWidgetGridRowsNumber } from '../helpers';
    import {
        DATE_PERIOD_MAPPING,
        DATE_PERIOD_YESTERDAY,
        DATE_PERIOD_TODAY,
        DATE_PERIOD_LAST_WEEK,
        DATE_PERIOD_LAST_MONTH,
        DATE_PERIOD_LAST_YEAR,
        DATE_PERIOD_THIS_WEEK,
        DATE_PERIOD_THIS_MONTH,
        DATE_PERIOD_THIS_YEAR,
    } from '../../../../constants';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

    export default {
        name: 'GuestTransactionsTableCardGridItem',
        mixins: [cardGridItemBaseMixin],

        components: { CardGrid, BusyModal, CardGridItem, TabWidgetSettingsModalForm, noteCellRenderer: NoteCellRenderer },
        data() {
            return {
                busy: false,
                rowData: [],
                paymentMethods: {},
                currentFilter: DATE_PERIOD_THIS_MONTH,
                limit: getWidgetGridRowsNumber({ tabWidget: this.tabWidget }),
            };
        },
        mounted() {
            this.fetchData();
        },
        computed: {
            ...mapGetters('header', ['userPermissions']),
            columnDefs() {
                return [
                    {
                        field: 'date',
                        headerName: 'Date',
                        minWidth: 100,
                        sort: 'desc',
                    },
                    {
                        field: 'guest_name',
                        headerName: 'Guest Name',
                        minWidth: 100,
                    },
                    {
                        field: 'invoice_id',
                        headerName: 'Invoice #',
                        minWidth: 100,
                        cellRenderer: ({ data, node }) => {
                            if (node.rowPinned) return '';
                            return `
                            <a
                                target="_blank"
                                href="/app/guest/${data.guest_id}/invoices/?reservation=${data.invoice_id}&tab=transactions"
                            >
                                ${data.invoice_id}
                            </a>`;
                        },
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        minWidth: 100,
                        cellStyle: ({ value }) => {
                            if (value === 'Cancelled' || value === 'Deleted') {
                                return { color: 'red' };
                            }
                            return null;
                        },
                    },
                    {
                        field: 'property',
                        headerName: 'Property',
                        minWidth: 110,
                    },
                    ...Object.keys(this.paymentMethods).map((pm_key) => {
                        const pm = this.paymentMethods[pm_key];
                        return {
                            field: pm_key,
                            headerName: pm,
                            type: 'amountColumn',
                            cellStyle: ({ data, node }) => {
                                const value = data[pm_key] || 0;
                                if (value < 0) return { color: 'red' };
                                return null;
                            },
                            minWidth: 124,
                        };
                    }),
                    {
                        field: 'total',
                        headerName: 'Total',
                        type: 'amountColumn',
                        cellStyle: ({ data }) => {
                            const { total } = data;
                            if (total < 0) return { color: 'red' };
                            return null;
                        },
                        minWidth: 100,
                        hide: Object.keys(this.paymentMethods).length < 2,
                    },
                    {
                        field: 'type',
                        headerName: 'Processing Type',
                        minWidth: 110,
                    },
                    {
                        field: 'sequence',
                        headerName: 'Sequence',
                        minWidth: 100,
                    },
                    {
                        field: 'transaction_code',
                        headerName: 'Transaction ID',
                        minWidth: 120,
                    },
                    {
                        field: 'notes',
                        headerName: 'Notes',
                        minWidth: 80,
                        autoHeight: true,
                        cellRenderer: 'noteCellRenderer',
                        autoWidthExclude: true,
                    },
                ];
            },
            filters() {
                return [
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_YESTERDAY],
                        filter: DATE_PERIOD_YESTERDAY,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_TODAY],
                        filter: DATE_PERIOD_TODAY,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_WEEK],
                        filter: DATE_PERIOD_THIS_WEEK,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_WEEK],
                        filter: DATE_PERIOD_LAST_WEEK,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_MONTH],
                        filter: DATE_PERIOD_THIS_MONTH,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_MONTH],
                        filter: DATE_PERIOD_LAST_MONTH,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_YEAR],
                        filter: DATE_PERIOD_THIS_YEAR,
                    },
                    {
                        title: DATE_PERIOD_MAPPING[DATE_PERIOD_LAST_YEAR],
                        filter: DATE_PERIOD_LAST_YEAR,
                    },
                ];
            },
            title() {
                const currentTitle = this.filters.find((item) => item.filter === this.currentFilter);
                return (this.tabWidget.settings.name || this.defaultName) + ' - ' + currentTitle.title;
            },
            tabWidgetSettingsInitialValues() {
                return {
                    settings: {
                        name: this.tabWidget.settings.name || this.defaultName,
                        table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                    },
                };
            },
        },
        methods: {
            refresh() {
                this.fetchData();
                this.cardGridKey++;
            },
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_GUEST_TRANSACTIONS_ENDPOINT('row-data'), {
                        params: { period: this.currentFilter, properties: '' },
                    });
                    const { transactions, total, payment_table, payment_methods } = data;
                    this.paymentMethods = payment_methods;
                    this.rowData = [
                        ...transactions.map((transaction, index) => ({
                            id: index,
                            ...transaction,
                            ...transaction.payment_table,
                        })),
                    ];
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data.' });
                } finally {
                    this.busy = false;
                }
            },
            setFilter(filter) {
                this.currentFilter = filter;
                this.fetchData();
                this.handleResized();
            },
            handleResized() {
                this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
                this.cardGridKey++;
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.btn) {
        padding: 0.12rem 0.25rem !important;
        font-size: 0.775rem !important;
    }
</style>
